import React, { useMemo, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Badge, Box, Checkbox, Chip, CircularProgress, FormControl,
  IconButton, InputLabel, Link, MenuItem, Paper, Select, Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import { TaskDialog, useRoadmapProgress } from "./RoadmapTodo";
import {
  INTENTS, KEYWORDS, PRIORITY_LABELS, flattenKeywordActions, keywordTaskId, rankColor, rankLabel, rankSummary, sortKeywords,
} from "./marketingKeywordRankingsData";

const PURPLE = "#7c3aed";
const DONE_GREEN = "#047857";
const INTENT_BY_ID = Object.fromEntries(INTENTS.map((i) => [i.id, i]));

// ---------- pure helpers (exported for tests) ----------

export function filterKeywords(list, { status = "all", intent = "all", search = "" }, states = {}) {
  const q = search.trim().toLowerCase();
  return list
    .map((k) => {
      const actions = k.actions.filter((act) => {
        const done = !!states[keywordTaskId(k.id, act.id)]?.completed;
        if (status === "open" && done) return false;
        if (status === "done" && !done) return false;
        return true;
      });
      return { ...k, visibleActions: actions };
    })
    .filter((k) => {
      if (intent !== "all" && k.intent !== intent) return false;
      if (q && ![k.keyword, k.leader.name, k.leader.site, k.gap, ...k.actions.map((x) => x.title)].join(" ").toLowerCase().includes(q)) return false;
      if (status !== "all" && !k.visibleActions.length) return false;
      return true;
    });
}

function formatYmd(ymd) {
  const d = new Date(`${ymd}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? ymd : new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(d);
}

function ActionRow({ task, state, disabled, busy, onToggle, onComments }) {
  const done = !!state?.completed;
  const count = state?.comment_count || 0;
  return (
    <Box data-testid={`seo-task-${task.id}`} sx={{ display: "flex", gap: 1, py: 0.75, borderTop: "1px dashed #eef0f4", "&:first-of-type": { borderTop: 0 } }}>
      <Checkbox
        size="small"
        checked={done}
        disabled={disabled || busy}
        onChange={(e) => onToggle(task, e.target.checked)}
        icon={<CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: PURPLE }} />}
        checkedIcon={<CheckBoxIcon fontSize="small" sx={{ color: DONE_GREEN }} />}
        inputProps={{ "aria-label": `Mark done: ${task.title}` }}
        sx={{ p: 0.5, alignSelf: "flex-start" }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, textDecoration: done ? "line-through" : "none", color: done ? "text.secondary" : "text.primary" }}>
          {task.title}
          {task.effort && <Chip size="small" variant="outlined" label={`~${task.effort}`} sx={{ ml: 1, height: 20, fontSize: 11 }} />}
        </Typography>
        {task.detail && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{task.detail}</Typography>}
        {done && (
          <Typography variant="caption" sx={{ display: "block", color: DONE_GREEN, fontWeight: 700 }} data-testid={`seo-done-${task.id}`}>
            Done{state.completed_by?.name ? ` by ${state.completed_by.name}` : ""}
          </Typography>
        )}
      </Box>
      <Tooltip title="Comments and files">
        <span>
          <IconButton size="small" onClick={() => onComments(task)} disabled={disabled} aria-label={`Comments (${count}): ${task.title}`}>
            <Badge badgeContent={count} color="secondary" max={99}><ChatBubbleOutlineIcon fontSize="small" /></Badge>
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function StatChip({ label, value, testId, color = PURPLE }) {
  return <Chip data-testid={testId} label={`${label}: ${value}`} sx={{ fontWeight: 700, bgcolor: `${color}14`, color }} />;
}

export default function MarketingKeywordRankings() {
  const progress = useRoadmapProgress();
  const [filters, setFilters] = useState({ status: "all", intent: "all", search: "" });
  const [dialog, setDialog] = useState({ task: null, mode: "comments" });
  const [busyKey, setBusyKey] = useState(null);
  const [toast, setToast] = useState(null);

  const sorted = useMemo(() => sortKeywords(KEYWORDS), []);
  const allActions = useMemo(() => flattenKeywordActions(), []);
  const summary = useMemo(() => rankSummary(), []);
  const disabled = progress.loading || !!progress.error;
  const openCount = allActions.filter((t) => !progress.tasks[t.id]?.completed).length;
  const shown = filterKeywords(sorted, filters, progress.tasks);
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const onToggle = async (task, completed) => {
    setBusyKey(task.id);
    try {
      await progress.toggle(task.id, completed);
      if (completed) setDialog({ task: { ...task, action: task.title }, mode: "done" });
      else setToast({ severity: "info", text: "Action reopened." });
    } catch (e) {
      setToast({ severity: "error", text: e?.response?.data?.message || "Could not update the action." });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <AnalyticsPageShell
      title="Keyword rankings"
      subtitle="The Google searches parents use for 11+ prep, who leads each one, where 11plusmagic.co.uk stands, and the actions to climb."
    >
      <Alert severity="info" sx={{ mb: 2 }} data-testid="seo-estimate-alert">
        Ranks and volumes are seed estimates from the last manual check, not a live SERP feed. Re-check in a private Google UK search and update the data file.
      </Alert>

      <Paper sx={{ ...CARD_SX, p: 2, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
          <StatChip label="Keywords tracked" value={summary.total} testId="seo-stat-total" />
          <StatChip label="Top 3" value={summary.top3} testId="seo-stat-top3" color={DONE_GREEN} />
          <StatChip label="Top 10" value={summary.top10} testId="seo-stat-top10" color="#b45309" />
          <StatChip label="Not ranked" value={summary.unranked} testId="seo-stat-unranked" color="#64748b" />
          <StatChip label="Open climb actions" value={`${openCount}/${allActions.length}`} testId="seo-stat-open" />
          {progress.loading && <CircularProgress size={16} sx={{ alignSelf: "center" }} />}
        </Stack>
        {progress.error && <Alert severity="warning" sx={{ mt: 1 }}>{progress.error}</Alert>}
      </Paper>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 1.5 }}>
        <ToggleButtonGroup size="small" exclusive value={filters.status} onChange={(_, v) => v && set({ status: v })} aria-label="Status filter">
          <ToggleButton value="all" sx={{ textTransform: "none", px: 1.5 }}>All</ToggleButton>
          <ToggleButton value="open" sx={{ textTransform: "none", px: 1.5 }}>Open</ToggleButton>
          <ToggleButton value="done" sx={{ textTransform: "none", px: 1.5 }}>Done</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="seo-intent-label">Intent</InputLabel>
          <Select labelId="seo-intent-label" label="Intent" value={filters.intent} onChange={(e) => set({ intent: e.target.value })} inputProps={{ "data-testid": "seo-intent-select" }}>
            <MenuItem value="all">All intents</MenuItem>
            {INTENTS.map((i) => <MenuItem key={i.id} value={i.id}>{i.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Search" value={filters.search} onChange={(e) => set({ search: e.target.value })} inputProps={{ "data-testid": "seo-search" }} />
        <Typography variant="caption" color="text.secondary">Ticks and comments are shared with every admin.</Typography>
      </Stack>

      {!shown.length && <Typography variant="body2" color="text.secondary" data-testid="seo-empty">No keywords match the filters.</Typography>}

      {shown.map((k) => {
        const intent = INTENT_BY_ID[k.intent];
        const done = k.actions.filter((act) => progress.tasks[keywordTaskId(k.id, act.id)]?.completed).length;
        return (
          <Accordion key={k.id} disableGutters sx={{ ...CARD_SX, mb: 1, "&:before": { display: "none" } }} data-testid={`seo-keyword-${k.id}`}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>{k.keyword}</Typography>
                <Typography variant="caption" color="text.secondary">{k.volumeNote}</Typography>
                {intent && <Chip size="small" label={intent.label} sx={{ height: 20, fontSize: 11, bgcolor: `${intent.color}14`, color: intent.color, fontWeight: 600 }} />}
                <Chip size="small" variant="outlined" label={`${PRIORITY_LABELS[k.priority] || k.priority} priority`} sx={{ height: 20, fontSize: 11 }} />
                <Chip
                  size="small"
                  data-testid={`seo-rank-${k.id}`}
                  label={`Us: ${rankLabel(k.ourRank)}`}
                  sx={{ height: 20, fontSize: 11, fontWeight: 700, color: "#fff", bgcolor: rankColor(k.ourRank) }}
                />
                <Box sx={{ flex: 1 }} />
                <Chip
                  size="small"
                  data-testid={`seo-progress-${k.id}`}
                  label={`${done}/${k.actions.length} done`}
                  sx={{ fontWeight: 700, bgcolor: done === k.actions.length ? "#ecfdf5" : "#f5f3ff", color: done === k.actions.length ? DONE_GREEN : PURPLE }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Leader:</strong> {k.leader.name} (<Link href={`https://${k.leader.site}`} target="_blank" rel="noopener noreferrer">{k.leader.site}</Link>) — {k.leader.note}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Us:</strong> {rankLabel(k.ourRank)} for <Link href={k.ourUrl} target="_blank" rel="noopener noreferrer">{k.ourUrl}</Link>
                <Typography component="span" variant="caption" color="text.secondary"> · {k.rankSource}, checked {formatYmd(k.lastChecked)}</Typography>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}><strong>Gap:</strong> {k.gap}</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PURPLE }}>Climb actions</Typography>
              {k.visibleActions.length ? k.visibleActions.map((act) => {
                const task = { ...act, id: keywordTaskId(k.id, act.id) };
                return (
                  <ActionRow
                    key={task.id}
                    task={task}
                    state={progress.tasks[task.id]}
                    disabled={disabled}
                    busy={busyKey === task.id}
                    onToggle={onToggle}
                    onComments={(t) => setDialog({ task: { ...t, action: t.title }, mode: "comments" })}
                  />
                );
              }) : <Typography variant="caption" color="text.secondary">No actions match the status filter.</Typography>}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <TaskDialog
        task={dialog.task}
        mode={dialog.mode}
        comments={dialog.task ? progress.comments[dialog.task.id] || [] : []}
        onClose={() => setDialog({ task: null, mode: "comments" })}
        onSubmit={async (task, body, files) => { await progress.addComment(task.id, body, files); setToast({ severity: "success", text: "Comment added." }); }}
        onDelete={async (comment) => {
          try { await progress.deleteComment(comment); setToast({ severity: "success", text: "Comment deleted." }); } catch (e) { setToast({ severity: "error", text: e?.response?.data?.message || "Could not delete the comment." }); }
        }}
        onError={(text) => setToast({ severity: "error", text })}
      />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert onClose={() => setToast(null)} severity={toast.severity} variant="filled" sx={{ width: "100%" }}>{toast.text}</Alert> : <span />}
      </Snackbar>
    </AnalyticsPageShell>
  );
}
